var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { axiosInstance as axios } from './util/axios';
import { getPageviewProps, hasRequiredFields, isValidURL, logToConsole, sleep, getCurrentTimestamp, isBot, } from './util';
import { sessionService } from './SessionService';
import { isSingleParam } from './util';
import { COMMON_PROPS_REFETCH_TIMEOUT, HALF_HOUR, SESSION_TOKEN_REVALIDATION_TIMEOUT, PACKAGE_VERSION, EVENT_THROTTLE_LIMIT, } from './util/constants';
import { getBrowserProps } from './util/getPageviewProps';
import { parseExpiry } from './util/parseExpiry/parseExpiry';
import { activityTrackerEvents } from './activity-tracking';
import { getExperimentLogPayload } from './util/getExperimentLogData';
import { throttle } from './util';
import { generateUUID } from './util/generateUUID';
export class TuringClientLogger {
    constructor(options) {
        var _a, _b;
        this.options = {
            baseApiUrl: '',
            logName: 'TURING.COM',
        };
        this.firstRequestDone = false;
        this.firstRequestPending = false;
        this.lastRequestTimestamp = 0;
        this.commonPropsPending = false;
        this.commonPropsDone = false;
        this.commonPropsError = false;
        this.commonPropsTimestamp = 0;
        this.commonProps = {};
        this.seq = 0;
        this.eventListeners = [];
        this._isBot = false;
        this.isBotReason = '';
        this.botMachineId = '';
        this.isSessionExpired = () => getCurrentTimestamp() - this.lastRequestTimestamp >
            SESSION_TOKEN_REVALIDATION_TIMEOUT;
        this.areCommonPropsExpired = () => getCurrentTimestamp() - this.commonPropsTimestamp > HALF_HOUR;
        this.shouldRefetchCommonProps = () => this.commonPropsError &&
            getCurrentTimestamp() - this.commonPropsTimestamp >
                COMMON_PROPS_REFETCH_TIMEOUT;
        if (options.baseApiUrl && !isValidURL(options.baseApiUrl)) {
            throw new Error('Invalid URL');
        }
        if (!options.logName) {
            throw new Error('LogName is a required Param.');
        }
        void isBot().then(({ isBot, reason, machineId }) => {
            this._isBot = isBot;
            this.isBotReason = reason || '';
            this.botMachineId = machineId || '';
        });
        this.options = options;
        axios.defaults.validateStatus = () => true;
        axios.defaults.baseURL =
            (_a = this.options.baseApiUrl) !== null && _a !== void 0 ? _a : (_b = process.env) === null || _b === void 0 ? void 0 : _b.API_GATEWAY;
        axios.defaults.auth = {
            username: 'clientUser',
            password: 'Qok!233!!#3al',
        };
        axios.defaults.headers.common['x-client-logging-version'] = PACKAGE_VERSION;
        axios.defaults.headers.common['x-product-name'] = this.options.logName;
        axios.defaults.headers.common['x-log-name'] = this.options.logName;
        axios.interceptors.request.use((config) => __awaiter(this, void 0, void 0, function* () {
            if (this.isSessionExpired()) {
                this.firstRequestDone = false;
            }
            if (config.url === '/identify' || config.url === '/identify/remove') {
                this.firstRequestDone = false;
            }
            if (config.headers) {
                if (config.data.logName) {
                    config.headers['x-log-name'] = config.data.logName;
                }
                if (!config.headers['visitor-token']) {
                    config.headers['visitor-token'] = sessionService.getToken();
                }
                config.headers['x-is-bot'] = this._isBot ? 'true' : 'false';
                if (this.isBotReason) {
                    config.headers['x-is-bot-reason'] = this.isBotReason;
                }
                if (this.botMachineId) {
                    config.headers['x-bot-machine-id'] = this.botMachineId;
                }
            }
            if (this.firstRequestDone) {
                return config;
            }
            const isThrottledRequest = this.firstRequestPending;
            let limit = 30;
            while (this.firstRequestPending && limit--) {
                yield sleep(1000);
            }
            if (!this.firstRequestDone) {
                this.firstRequestPending = true;
            }
            if (isThrottledRequest && config.headers) {
                config.headers['visitor-token'] = sessionService.getToken();
            }
            return config;
        }));
        axios.interceptors.response.use((response) => __awaiter(this, void 0, void 0, function* () {
            var _c;
            if (!this.firstRequestDone) {
                if (response.status == 200 && ((_c = response.data) === null || _c === void 0 ? void 0 : _c.token)) {
                    sessionService.setToken(response.data.token);
                }
                this.firstRequestDone = true;
                this.firstRequestPending = false;
            }
            this.lastRequestTimestamp = getCurrentTimestamp();
            return response;
        }));
        sessionService.onTokenUpdate((token) => {
            axios.defaults.headers.common['visitor-token'] = token;
        });
    }
    log(message, log) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.logData({ message, log, severity: 'INFO' });
        });
    }
    warn(message, log) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.logData({ message, log, severity: 'WARNING' });
        });
    }
    error(message, log) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.logData({ message, log, severity: 'ERROR' });
        });
    }
    identify({ expiry: _expiry, userId, productName = this.options.logName, }) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const identity = sessionService.getIdentity(this.options.baseApiUrl, productName);
            if (identity &&
                identity.userId === userId &&
                identity.expiry > getCurrentTimestamp()) {
                return;
            }
            const expiry = parseExpiry(_expiry);
            const res = yield axios.post(`/identify`, {
                userId,
                productName,
                identityProps: {
                    expiry,
                },
                props: Object.assign(Object.assign({}, getBrowserProps()), { commonProps: yield this.getCommonProps(), productName, nonce: generateUUID() }),
            });
            if (res.status === 200 && ((_a = res.data) === null || _a === void 0 ? void 0 : _a.token)) {
                sessionService.setToken(res.data.token);
            }
        });
    }
    pageview() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.logData({
                message: {
                    eventName: 'PAGEVIEW',
                    logName: 'PAGEVIEWS',
                    logData: Object.assign(Object.assign({}, getPageviewProps()), { productName: this.options.logName, nonce: generateUUID() }),
                },
                severity: 'INFO',
            });
        });
    }
    logData({ message, severity, log }) {
        return __awaiter(this, void 0, void 0, function* () {
            const logData = Object.assign(Object.assign({}, message === null || message === void 0 ? void 0 : message.logData), { nonce: generateUUID(), commonProps: yield this.getCommonProps() });
            const registerPayload = isSingleParam(message)
                ? Object.assign(Object.assign({ logName: this.options.logName }, message), { logData,
                    severity }) : Object.assign(Object.assign({ logName: this.options.logName }, log), { logData,
                message,
                severity });
            yield this.register(registerPayload);
        });
    }
    register(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.message && typeof params.message === 'string') {
                logToConsole({ severity: params.severity, message: params.message });
            }
            try {
                params.callback && params.callback();
            }
            catch (e) {
                console.error(e.message);
            }
            if (!hasRequiredFields(params)) {
                throw new Error('turingLogger: Invalid payload. Make sure severity, eventName and logData fields are set');
            }
            if (params.logName === 'EXPERIMENTS')
                params.logData = getExperimentLogPayload(params.logData);
            const res = yield axios.post(`/log`, params, {
                headers: { 'x-seq': `${this.seq++}` },
            });
            if (res.status === 200 && ((_a = res.data) === null || _a === void 0 ? void 0 : _a.token)) {
                sessionService.setToken(res.data.token);
            }
        });
    }
    getCommonProps() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.areCommonPropsExpired() || this.shouldRefetchCommonProps()) {
                this.commonPropsDone = false;
            }
            if (this.commonPropsDone) {
                return this.commonProps;
            }
            let limit = 30;
            while (this.commonPropsPending && limit--) {
                yield sleep(1000);
            }
            if (!this.commonPropsDone) {
                this.commonPropsPending = true;
                if (this.options.getCommonProps) {
                    try {
                        this.commonProps = Object.assign(Object.assign({}, this.commonProps), (yield this.options.getCommonProps()));
                        this.commonPropsTimestamp = getCurrentTimestamp();
                        this.commonPropsError = false;
                    }
                    catch (e) {
                        this.commonPropsError = true;
                        this.commonProps = {};
                        void this.error({
                            eventName: 'LOGGING_COMMON_PROPS_ERROR',
                            logData: {
                                error: e.message,
                            },
                        });
                        console.error(e);
                    }
                }
                this.commonPropsDone = true;
                this.commonPropsPending = false;
            }
            return this.commonProps;
        });
    }
    setCommonProps(commonProps) {
        this.commonProps = Object.assign(Object.assign({}, this.commonProps), commonProps);
    }
    isLoggedIn(productName = this.options.logName) {
        const identity = sessionService.getIdentity(this.options.baseApiUrl, productName);
        if (!identity || identity.expiry < getCurrentTimestamp()) {
            return false;
        }
        return true;
    }
    isLoggedOut(productName = this.options.logName) {
        const identity = sessionService.getIdentity(this.options.baseApiUrl, productName);
        if (!identity) {
            return false;
        }
        if (identity.expiry < getCurrentTimestamp()) {
            return true;
        }
        return false;
    }
    removeIdentity(productName = this.options.logName) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield axios.post('/identify/remove', { productName });
            if (res.status === 200 && ((_a = res.data) === null || _a === void 0 ? void 0 : _a.token)) {
                sessionService.setToken(res.data.token);
            }
        });
    }
    getVisitorTokenPayload() {
        return sessionService.getTokenPayload();
    }
    getUserId(productName = this.options.logName) {
        const identity = sessionService.getIdentity(this.options.baseApiUrl, productName);
        if (!identity) {
            return null;
        }
        return identity.userId;
    }
    registerActivityTracker() {
        this.unregisterActivityTracker();
        activityTrackerEvents.map(([eventName, eventHandler]) => {
            const handler = throttle(this, (e) => eventHandler(e, this), EVENT_THROTTLE_LIMIT);
            window.addEventListener(eventName, handler, true);
            this.eventListeners.push([eventName, handler]);
        });
    }
    unregisterActivityTracker() {
        this.eventListeners.map(([eventName, eventHandler]) => window.removeEventListener(eventName, eventHandler, true));
        this.eventListeners = [];
    }
    isBot() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield isBot();
            if (!result.isBot) {
                return { isBot: false };
            }
            return {
                isBot: true,
                reason: result.reason || 'unknown',
            };
        });
    }
}
//# sourceMappingURL=TuringClient.js.map